/**
 * Where an anon's preview of a result ends: the first two sentences at least, then up to
 * PREVIEW_SHARE of the text but no more than PREVIEW_MAX_CHARS — never the whole text.
 */

export const PREVIEW_MIN_SENTENCES = 2;
export const PREVIEW_SHARE = 0.3;
export const PREVIEW_MAX_CHARS = 1000;

const hasSegmenter = typeof Intl !== 'undefined' && 'Segmenter' in Intl;

// The result's sentences can run long (the model hands some languages back as one), so the
// text's own sentences, by the locale-aware segmenter, count too.
// A sentence ends at its last character, not after the space that follows it.
const sentenceBoundaries = (text: string, resultEnds: number[]): number[] => {
    const own = hasSegmenter
        ? Array.from(new Intl.Segmenter(undefined, {granularity: 'sentence'}).segment(text), s => s.index + s.segment.length)
        : [];
    const ends = [...resultEnds, ...own].map(at => text.slice(0, at).trimEnd().length);
    return Array.from(new Set(ends)).sort((a, b) => a - b);
};

const wordBoundaries = (text: string): number[] => {
    // Intl.Segmenter finds words in scripts written without spaces (Chinese, Japanese, Thai).
    if (hasSegmenter) {
        const words = new Intl.Segmenter(undefined, {granularity: 'word'}).segment(text);
        // A word's end, and not before punctuation: a full stop never opens the closed rest.
        return Array.from(words, w => w.index).filter(i => i > 0 && !/\s/.test(text[i - 1]) && !/\p{P}/u.test(text[i]));
    }
    return Array.from(text.matchAll(/\s+/g), m => m.index!).filter(i => i > 0);
};

/**
 * The UTF-16 offset the preview of `text` ends at. `sentenceEnds` are the offsets the result's
 * sentences end at. Past the first two sentences the cut takes the last paragraph end within
 * the share, else the last sentence end, else the last word end. Null when the text has
 * nothing to split: a single word.
 */
export const previewCut = (text: string, sentenceEnds: number[]): number | null => {
    const first = text.search(/\S/), end = text.trimEnd().length;
    // A cut has to leave words on both of its sides.
    const splits = (at: number) => at > first && at < end;
    const sentences = sentenceBoundaries(text, sentenceEnds).filter(splits);
    const words = wordBoundaries(text).filter(splits);
    const share = Math.min((end - Math.max(first, 0)) * PREVIEW_SHARE, PREVIEW_MAX_CHARS);
    // A text of fewer sentences than the minimum keeps its last one closed.
    const least = sentences[Math.min(PREVIEW_MIN_SENTENCES, sentences.length) - 1] ?? 0;
    if (least >= share) return least;

    const paragraphEnds = Array.from(text.matchAll(/\S(?=[^\S\n]*\n)/g), m => m.index! + 1).filter(splits);
    const last = (offsets: number[]) => offsets.filter(at => at >= least && at <= share).pop();
    return last(paragraphEnds) ?? last(sentences) ?? last(words) ?? words[0] ?? null;
};
